/**
 * 홈 — 동네 학원 디렉토리 (런즈 004 스타일)
 * 검색 + 카테고리 + 슬라이드 배너 + 학원 카드. 흰 배경, 이모지 없음.
 * 카드 탭 → 학원 상세(소개). 가입은 상세 안에서.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { IoSearch, IoChatbubbleEllipsesOutline, IoPersonOutline, IoClose } from 'react-icons/io5';
import { useAuth } from '../../context/AuthContext';
import { useAcademy } from '../../context/AcademyContext';
import academyService from '../../services/academyService';
import CompanyFooter from '../../components/common/CompanyFooter';
import ChildrenSheet from '../../components/parent/ChildrenSheet';

const CATEGORIES = [
  { key: 'all', label: '전체', bg: '#F4F5F7' },
  { key: '영어', label: '영어', img: '/cat/english.png', bg: '#E5EEFB' },
  { key: '수학', label: '수학', img: '/cat/math.png', bg: '#E5F1EC' },
  { key: '논술', label: '논술', img: '/cat/essay.png', bg: '#EDEBFB' },
  { key: '피아노', label: '피아노', img: '/cat/piano.png', bg: '#FBEAF1' },
  { key: '미술', label: '미술', img: '/cat/art.png', bg: '#FDEEE0' },
  { key: '복싱', label: '복싱', img: '/cat/boxing.png', bg: '#FBE9E9' },
  { key: '태권도', label: '태권도', img: '/cat/taekwondo.png', bg: '#E2F0F1' },
  { key: '줄넘기', label: '줄넘기', img: '/cat/jumprope.png', bg: '#E5EEFB' },
  { key: '무술', label: '무술', img: '/cat/martial.png', bg: '#EAEAFA' },
];
const FIELD_NAME = { study: '교과 과정', sports: '운동 / 태권도', arts: '음악 / 미술' };

// 학원 목록 캐시 — 홈 재진입 시 스피너 없이 즉시 표시
let academiesCache = null;

// 배너 슬라이드 — 추후 실제 이미지로 교체
const BANNERS = [
  { title: '내 아이를 1등급으로', sub: '우리 동네 학원을 한눈에', img: '/banner/1.webp', bg: '#ECEBFB' },
  { title: '찾고, 신청하고, 다니고', sub: '동네 학원이 여기 다 있어요', img: '/banner/2.webp', bg: '#E5F1EC' },
  { title: '출석부터 성적까지', sub: '학원 생활을 한곳에서', img: '/banner/3.webp', bg: '#FBEEE3' },
];

const AppHomePage = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const { mode } = useAcademy();

  const [academies, setAcademies] = useState(academiesCache || []);
  const [loading, setLoading] = useState(!academiesCache);
  const [kw, setKw] = useState('');
  const [cat, setCat] = useState('all');
  const [slide, setSlide] = useState(0);
  const [childBannerOff, setChildBannerOff] = useState(false);
  const [childSheetOpen, setChildSheetOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await academyService.getApprovedAcademies();
        if (!alive) return;
        academiesCache = data;
        setAcademies(data);
      } catch (e) {
        console.error('학원 목록 로드 실패:', e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % BANNERS.length), 4000);
    return () => clearInterval(t);
  }, []);

  // 데모 체험 중인 학부모(개인 모드)는 '팩토수학'만 노출
  const isDemo = userData?.provider === 'demo';
  const list = useMemo(() => {
    let r = academies;
    if (isDemo && mode === 'personal') r = r.filter((a) => a.name === '팩토수학');
    if (cat !== 'all') r = r.filter((a) => a.subject === cat);
    if (kw.trim()) r = r.filter((a) => (a.name || '').includes(kw.trim()));
    return r;
  }, [academies, cat, kw, isDemo, mode]);

  // 학부모(개인 모드) 로그인 + 자녀 없음 → 자녀 등록 안내
  const showChildBanner = !childBannerOff
    && !!userData
    && mode === 'personal'
    && (userData.children || []).length === 0;

  return (
    <Container>
      <BrandHeader>
        <BrandName>일등급</BrandName>
        <BrandSub>우리 동네 학원 관리 · 소통 서비스</BrandSub>
      </BrandHeader>

      <SearchBar>
        <IoSearch size={18} color="#9CA3AF" />
        <SearchInput value={kw} onChange={(e) => setKw(e.target.value)} placeholder="학원 이름 검색" />
      </SearchBar>

      {showChildBanner && (
        <ChildBanner onClick={() => setChildSheetOpen(true)}>
          <IoPersonOutline size={20} />
          <ChildBannerText>자녀를 등록하고 학원 수강을 신청해보세요</ChildBannerText>
          <ChildBannerX onClick={(e) => { e.stopPropagation(); setChildBannerOff(true); }}>
            <IoClose size={16} />
          </ChildBannerX>
        </ChildBanner>
      )}

      <ChildrenSheet
        open={childSheetOpen}
        onClose={() => setChildSheetOpen(false)}
      />

      <Categories>
        {CATEGORIES.map((c) => (
          <CatItem
            key={c.key}
            onClick={() => (c.key === 'community' ? navigate('/app/community') : setCat(c.key))}
          >
            <CatTile $active={cat === c.key} $bg={c.bg}>
              {c.key === 'all' && <CatAll>ALL</CatAll>}
              {c.key === 'community' && <IoChatbubbleEllipsesOutline size={26} color="#4F46E5" />}
              {c.img && <CatImg src={c.img} alt={c.label} />}
            </CatTile>
            <CatLabel $active={cat === c.key}>{c.label}</CatLabel>
          </CatItem>
        ))}
      </Categories>

      <Banner>
        <BannerTrack style={{ transform: `translateX(-${slide * 100}%)` }}>
          {BANNERS.map((b, i) => (
            <Slide key={i} style={{ backgroundImage: `url(${b.img})`, backgroundColor: b.bg }}>
              <SlideTitle>{b.title}</SlideTitle>
              <SlideSub>{b.sub}</SlideSub>
            </Slide>
          ))}
        </BannerTrack>
        <Dots>
          {BANNERS.map((_, i) => <Dot key={i} $on={i === slide} />)}
        </Dots>
      </Banner>

      {userData?.provider !== 'demo' && (
        <DemoTeaser type="button" onClick={() => navigate('/demo')}>
          <DemoTeaserText>
            <DemoTeaserLabel>1등급 둘러보기</DemoTeaserLabel>
            <DemoTeaserTitle>
              {userData?.accountType === 'academy'
                ? '학원 운영에 필요한 1등급 기능을 확인해보세요'
                : '학부모를 위한 1등급 기능을 확인해보세요'}
            </DemoTeaserTitle>
          </DemoTeaserText>
        </DemoTeaser>
      )}

      <SectionTitle>
        {cat === 'all' ? '우리 동네 학원' : `${cat} 학원`}
        <Count>{list.length}</Count>
      </SectionTitle>

      {loading ? null : list.length === 0 ? (
        <Hint>해당하는 학원이 없어요.</Hint>
      ) : (
        list.map((a) => {
          return (
            <Card key={a.id} onClick={() => navigate(`/app/academy/${a.id}`, { state: { subject: a.subject } })}>
              <CardBody>
                <CardTop>
                  <CardName>{a.name}</CardName>
                </CardTop>
                <CardHighlight>{FIELD_NAME[a.field]} · {a.subject}</CardHighlight>
                {a.intro && <CardDesc>{a.intro}</CardDesc>}
                <Tags>
                  <Tag>{FIELD_NAME[a.field]}</Tag>
                  <Tag>{a.type === 'home' ? '교습소' : a.type === 'personal' ? '개인레슨' : '학원'}</Tag>
                  {a.subject && <Tag>{a.subject}</Tag>}
                </Tags>
              </CardBody>
              <CardImage $url={a.photoUrl}>{!a.photoUrl && (a.name || '?').slice(0, 1)}</CardImage>
            </Card>
          );
        })
      )}
      <CompanyFooter />
    </Container>
  );
};

export default AppHomePage;

const Container = styled.div`
  max-width: 480px;
  margin: 0 auto;
  padding: 16px 16px 24px;
  background: #fff;
`;

const BrandHeader = styled.div`
  padding: 4px 2px 14px;
`;

const BrandName = styled.p`
  font-size: 24px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
  letter-spacing: -0.3px;
`;

const BrandSub = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: #6B7280;
  margin-top: 3px;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  background: #F4F5F7;
  border-radius: 12px;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: none;
  font-size: 14px;
  &:focus { outline: none; }
`;

const ChildBanner = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 13px 14px;
  border-radius: 12px;
  background: #22B07D;
  color: #fff;
  text-align: left;
`;

const ChildBannerText = styled.span`
  flex: 1;
  font-size: 13px;
  font-weight: 700;
`;

const ChildBannerX = styled.span`
  display: flex;
  padding: 2px;
  opacity: 0.85;
`;

const Categories = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 4px 0;
  margin: 16px 0 8px;
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const CatItem = styled.button`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  background: none;
`;

const CatTile = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $active, $bg, theme }) => ($active ? theme.colors.primaryLight : ($bg || '#F4F5F7'))};
  transition: background 0.15s;
`;

const CatImg = styled.img`
  width: 78%;
  height: 78%;
  object-fit: contain;
`;

const CatAll = styled.span`
  font-size: 15px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
`;

const CatLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : '#9CA3AF')};
`;

const Banner = styled.div`
  margin: 18px 0;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
`;

const BannerTrack = styled.div`
  display: flex;
  transition: transform 0.5s ease;
`;

const Slide = styled.div`
  min-width: 100%;
  height: 96px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  background-color: #F1F2F8;
`;

const SlideTitle = styled.p`
  font-size: 18px;
  font-weight: 800;
  color: #1F2330;
`;

const SlideSub = styled.p`
  font-size: 12.5px;
  color: #5B6473;
  margin-top: 4px;
`;

const Dots = styled.div`
  position: absolute;
  bottom: 12px;
  right: 16px;
  display: flex;
  gap: 5px;
`;

const shimmer = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(74, 124, 254, 0.35); }
  50%      { box-shadow: 0 0 0 8px rgba(74, 124, 254, 0); }
`;

const DemoTeaser = styled.button`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  margin: 14px 0 18px;
  border-radius: 14px;
  background:
    linear-gradient(
      110deg,
      ${({ theme }) => theme.colors.primaryLight} 0%,
      #ffffff 50%,
      ${({ theme }) => theme.colors.primaryLight} 100%
    );
  background-size: 200% 100%;
  animation: ${shimmer} 2.6s linear infinite, ${pulseGlow} 2.6s ease-in-out infinite;
  text-align: left;
  overflow: hidden;
  &:active {
    transform: scale(0.98);
  }
`;

const DemoTeaserText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DemoTeaserLabel = styled.span`
  font-size: 11.5px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
`;

const DemoTeaserTitle = styled.span`
  font-size: 13.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;


const Dot = styled.span`
  width: ${({ $on }) => ($on ? '16px' : '6px')};
  height: 6px;
  border-radius: 3px;
  background: ${({ $on }) => ($on ? '#fff' : 'rgba(255,255,255,0.45)')};
  transition: width 0.3s;
`;

const SectionTitle = styled.p`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 8px 0 12px;
`;

const Count = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.accent.gold};
`;

const Card = styled.div`
  display: flex;
  gap: 14px;
  padding: 24px 0;
  border-bottom: 1px solid #EEF0F3;
  cursor: pointer;
`;

const CardBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CardName = styled.p`
  font-size: 17px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CardHighlight = styled.p`
  font-size: 13px;
  font-weight: 700;
  color: #F59B95;
  margin-top: 5px;
`;

const CardDesc = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 7px;
  line-height: 1.55;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 11px;
`;

const Tag = styled.span`
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #6B7280;
  background: #F4F5F7;
`;

const CardImage = styled.div`
  width: 116px;
  height: 116px;
  flex-shrink: 0;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 30px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ $url }) => ($url ? `url(${$url}) center/cover` : '#EDEFF3')};
`;

const Hint = styled.p`
  text-align: center;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.hint};
  padding: 50px 0;
`;
